import { IconButton, TextField, useTheme } from "@mui/material"
import InputAdornment from "@mui/material/InputAdornment"
import React, { useEffect, useRef, useContext, useState } from "react"
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import { retrieveValue, storeObject } from "../../../../utility/storage";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";

export interface ISearchCriteria {
    search: string;
    exact: boolean;
    cancelled: boolean;
    show: boolean;
    reload?: boolean;
    expandedSearch?: boolean;
}

interface ISearchContext {
    criteria: ISearchCriteria,
    setCriteria: (criteria: ISearchCriteria) => void;
}

export const SearchContext = React.createContext<ISearchContext>({
    criteria: {} as ISearchCriteria,
    setCriteria: () => { console.warn("No search provider"); }
});

const SearchBooking = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDesktopWidth = useMediaQuery(theme.breakpoints.up('md'));
    const { hasPermission } = useUserPermissions();

    const searchInputRef = useRef<HTMLInputElement>(null);

    const focusInput = () => {
        if (searchInputRef.current != null) {
            searchInputRef.current.click();
            searchInputRef.current.focus();
        }
    }

    const searchContext = useContext(SearchContext);

    const [search, setSearch] = useState(searchContext.criteria.search || "");

    useEffect(() => {
        if (searchContext.criteria) {
            if (searchContext.criteria.show == true && isDesktopWidth == false) {
                if (searchInputRef.current != null) {
                    focusInput();
                }
            }
        }
    }, [searchInputRef, searchContext.criteria, isDesktopWidth]);

    const submitSearch = (search: string) => {
        if (searchInputRef.current != null) {
            const trimmedSearch = (search ?? '').trim();

            if (trimmedSearch.length == 0) {
                focusInput();
                return;
            }

            const searchParams = {
                search: trimmedSearch,
                cancelled: false,
                exact: true,
                show: true,
                reload: true,
            };

            searchContext.setCriteria({
                ...searchParams,
                show: true
            });

            if (retrieveValue("storeSearch", "1") == "1") {
                storeObject("search", searchParams);
            }
        }
    }

    if (!hasPermission("bookingManagement.booking.read")) {
        return null;
    }

    return <div>
        <form>

            <div>
                <TextField
                    autoFocus={false}
                    label={t("home.search.searchBookingsFieldLabel")}
                    variant="outlined"
                    size="small"
                    type="search"
                    style={{ width: isDesktopWidth ? "auto" : "100%" }}
                    inputRef={searchInputRef}
                    value={search}
                    onChange={() => {
                        if (searchInputRef.current) {
                            setSearch(searchInputRef.current.value);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (searchInputRef.current) {
                                searchInputRef.current.blur();
                                submitSearch(searchInputRef.current.value);
                            }
                            return false;
                        }

                    }}
                    onFocus={() => {
                        if (searchInputRef.current) {
                            searchInputRef.current.select();
                        }
                    }}

                    InputProps={{
                        endAdornment: (
                            <InputAdornment position={"end"}>
                                <IconButton style={{ padding: 0 }} onClick={() => {
                                    submitSearch(search);
                                }}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </div>
        </form>
    </div>
}

export default SearchBooking;
